import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import Swal from 'sweetalert2';
import { environment } from 'src/environments/environment';
import { DataTableDirective } from 'angular-datatables';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { DialogService } from 'src/app/layout/services/dialog.service';
import { PermisiuniService } from './permisiuni.service';
import { IPermisiune } from './permisiuni.interface';
import { AddPermisiuneComponent } from './add-permisiune/add-permisiune.component';
import { EditPermisiuneComponent } from './edit-permisiune/edit-permisiune.component';

@Component({
  selector: 'app-permisiuni',
  templateUrl: './permisiuni.component.html',
  styleUrls: ['./permisiuni.component.scss'],
})
export class PermisiuniComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(DataTableDirective, { static: false })
  dtElement!: DataTableDirective;

  isLoading = false;
  selectedSection: string = '';

  dtOptions: any = {};
  dtTrigger: Subject<any> = new Subject<any>();

  dtLink =
    environment.interop.basePath +
    environment.interop.urbanism.nomenclatoare.permissions.getUserPermissions +
    '-dt';

  dataTablesParameters: any = {
    data: {
      key: 'parameter',
    },
  };

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private permisiuniService: PermisiuniService
  ) {}

  ngOnInit(): void {
    this.renderDT();
  }

  ngOnDestroy(): void {
    this.dtTrigger.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.dtTrigger.next('');
  }

  renderDT() {
    this.isLoading = true;
    this.dtOptions = {
      serverSide: true, // Set the flag
      ajax: (dataTablesParameters: any, callback: any) => {
        // Adding custom parameters to the dataTablesParameters object
        dataTablesParameters.param1 = 'value1';
        dataTablesParameters.param2 = 'value2';
        dataTablesParameters.param3 = 'value3';

        this.http
          .post<DataTablesResponse>(
            this.dtLink, // datatable link, we need this
            dataTablesParameters // parameters for filtering, send here
          )
          .subscribe((resp: any) => {
            this.isLoading = false;
            callback({
              recordsTotal: resp.data.recordsTotal,
              recordsFiltered: resp.data.recordsFiltered,
              data: resp.data.data,
            });
          });
      },
      language: environment.config.romanianLanguageDataTable,
      columns: [
        {
          title: 'ID',
          data: 'id',
        },
        {
          title: 'Nume',
          data: 'name',
        },
        {
          title: 'Secțiune',
          data: 'section_id',
        },
        {
          title: 'Acțiuni',
          orderable: false,
          render: () => {
            return `
              <button class="action-btn edit">
                <img class="btn-img" src="../../../../assets/icons/edit.svg" alt="edit"/>
              </button>
              <button class="action-btn delete">
                <img class="btn-img" src="../../../../assets/icons/delete.svg" alt="delete"/>
              </button>
            `;
          },
        },
      ],
      rowCallback: (row: Node, data: any[] | any) => {
        const lastTd = (row as HTMLElement).querySelector('td:last-child');
        const editIcon = lastTd?.querySelector('.edit');
        const deleteIcon = lastTd?.querySelector('.delete');

        if (editIcon) {
          editIcon.addEventListener('click', () => {
            this.openEditDialog(data);
          });
        }

        if (deleteIcon) {
          deleteIcon.addEventListener('click', () => {
            const { id } = data;
            this.deleteSection(id);
          });
        }

        return row;
      },
    };
  }

  rerenderDT(): void {
    this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
      // Destroy the table first
      dtInstance.destroy();
      // Call the dtTrigger to rerender again
      this.dtTrigger.next('');
    });
  }

  deleteSection(permisiuneId: number) {
    Swal.fire({
      title: 'Sunteți sigur?',
      text: 'Nu o să puteți recupera datele odată cu ștergerea lor!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DD6B55',
      confirmButtonText: 'Confirm!',
      cancelButtonText: 'Anulare',
      showLoaderOnConfirm: true,
    }).then((result) => {
      if (result.isConfirmed) {
        this.permisiuniService.deletePermisiune(permisiuneId).subscribe(() => {
          this.rerenderDT();
          Swal.fire({
            title: 'Șters!',
            text: 'Permisiune ștearsă cu succes.',
            icon: 'success',
            confirmButtonText: 'OK',
          });
        });
      }
    });
  }

  openAddDialog() {
    const dialogRef = this.dialog.open(AddPermisiuneComponent, {
      ...this.dialogService.getDialogConfig(),
    });

    dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
      if (success) {
        this.rerenderDT();
      }
    });
  }

  openEditDialog(data: IPermisiune) {
    const dialogRef = this.dialog.open(EditPermisiuneComponent, {
      ...this.dialogService.getDialogConfig(),
      data,
    });

    dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
      if (success) {
        this.rerenderDT();
      }
    });
  }
}
